import React, { useState, useEffect } from 'react';
import { School, Search, CheckCircle, Archive } from 'lucide-react';
import Layout from '../components/Layout';
import api from '../components/api';
import { toast } from 'react-toastify';
import {
  SalleHeader,
  SalleStats,
  SalleSection,
  SalleForm,
  ConfirmDialog,
  Pagination
} from '../components/salle';

const SallePage = () => {
  const [salles, setSalles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedSalle, setSelectedSalle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    libelle: '',
    capacite: ''
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: null,
    salle: null
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchSalles();
  }, []);

  // Debounce search term
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const fetchSalles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/etablissement/salles/', {
        params: {
          page: currentPage,
          page_size: itemsPerPage
        }
      });
      
      const sallesData = response.data?.results || [];
      const paginationData = response.data;
      
      if (!Array.isArray(sallesData)) {
        setSalles([]);
        return;
      }
      
      if (paginationData) {
        setTotalPages(paginationData.count ? Math.ceil(paginationData.count / itemsPerPage) : 0);
        setTotalItems(paginationData.count || 0);
      }
      
      setSalles(sallesData);
    } catch (error) {
      toast.error('Erreur lors de la récupération des salles');
      setSalles([]);
    } finally {
      setLoading(false);
    }
  };

  // Recharger les salles quand la page change
  useEffect(() => {
    fetchSalles();
  }, [currentPage]);

  const handleOpenDialog = (salle = null) => {
    if (salle) {
      setIsEdit(true);
      setSelectedSalle(salle);
      setFormData({
        libelle: salle.libelle,
        capacite: salle.capacite
      });
    } else {
      setIsEdit(false);
      setSelectedSalle(null);
      setFormData({
        libelle: '',
        capacite: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      libelle: '',
      capacite: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.libelle.trim()) {
      toast.error('Le libellé de la salle est requis');
      return false;
    }
    if (!formData.capacite || parseInt(formData.capacite) < 1) {
      toast.error('La capacité doit être un nombre positif supérieur à 0');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const dataToSend = {
        ...formData,
        capacite: parseInt(formData.capacite)
      };

      let response;
      if (isEdit) {
        response = await api.put(`/etablissement/salles/${selectedSalle.id}/`, dataToSend);
        toast.success(`Salle "${response.data.libelle}" mise à jour avec succès`);
      } else {
        response = await api.post('/etablissement/salles/', dataToSend);
        toast.success(`Salle "${response.data.libelle}" créée avec succès`);
      }

      fetchSalles();
      handleCloseDialog();
    } catch (error) {
      if (error.response && error.response.data) {
        const errors = error.response.data;
        if (typeof errors === 'object') {
          Object.entries(errors).forEach(([field, message]) => {
            toast.error(`${field}: ${Array.isArray(message) ? message.join(', ') : message}`);
          });
        } else {
          toast.error('Erreur lors de la sauvegarde');
        }
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } finally {
      setLoading(false);
    }
  };

  const openConfirmDialog = (title, message, action, salle) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      action,
      salle
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      title: '',
      message: '',
      action: null,
      salle: null
    });
  };

  const handleArchiveSalle = async (salle) => {
    try {
      setLoading(true);
      await api.post(`/etablissement/salles/${salle.id}/archiver/`);
      toast.success(`Salle "${salle.libelle}" archivée avec succès`);
      fetchSalles();
      closeConfirmDialog();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage de la salle');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreSalle = async (salle) => {
    try {
      setLoading(true);
      await api.delete(`/etablissement/salles/${salle.id}/archiver/`);
      toast.success(`Salle "${salle.libelle}" restaurée avec succès`);
      fetchSalles();
      closeConfirmDialog();
    } catch (error) {
      toast.error('Erreur lors de la restauration de la salle');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSalle = async (salle) => {
    try {
      setLoading(true);
      await api.delete(`/etablissement/salles/${salle.id}/`);
      toast.success(`Salle "${salle.libelle}" supprimée avec succès`);
      fetchSalles();
      closeConfirmDialog();
    } catch (error) {
      toast.error('Erreur lors de la suppression de la salle');
    } finally {
      setLoading(false);
    }
  };

  // Pagination handler
  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Reset current page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Filtrer les salles en fonction du terme de recherche
  const filteredSalles = salles.filter(salle =>
    salle.libelle.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
  );

  // Séparer les salles actives et archivées
  const activeSalles = filteredSalles.filter(salle => !salle.is_archive);
  const archivedSalles = filteredSalles.filter(salle => salle.is_archive);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        <SalleHeader
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onAddClick={() => handleOpenDialog()}
        />

        {loading && salles.length === 0 ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
          </div>
        ) : salles.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <School className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Aucune salle de classe</h2>
            <p className="text-gray-500 mb-6">
              Vous n'avez pas encore créé de salle de classe. Commencez par en créer une.
            </p>
            <button
              onClick={() => handleOpenDialog()}
              className="inline-flex items-center bg-[#202654] text-white px-5 py-2 rounded-lg shadow-md hover:bg-[#2d347d] transition-all duration-200"
            >
              Créer une salle
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            <SalleStats
              activeSalles={activeSalles}
              archivedSalles={archivedSalles}
              filteredSalles={filteredSalles}
              debouncedSearchTerm={debouncedSearchTerm}
            />

            <SalleSection
              title="Salles actives"
              icon={CheckCircle}
              salles={activeSalles}
              isArchived={false}
              onEdit={handleOpenDialog}
              onArchive={(salle) => openConfirmDialog(
                'Archiver la salle',
                `Êtes-vous sûr de vouloir archiver la salle "${salle.libelle}" ?`,
                handleArchiveSalle,
                salle
              )}
              onDelete={(salle) => openConfirmDialog(
                'Supprimer la salle',
                `Êtes-vous sûr de vouloir supprimer définitivement la salle "${salle.libelle}" ? Cette action est irréversible.`,
                handleDeleteSalle,
                salle
              )}
              showPagination={activeSalles.length > 0}
              pagination={
                <Pagination 
                  totalPages={totalPages} 
                  currentPage={currentPage} 
                  paginate={paginate} 
                />
              }
            />

            <SalleSection
              title="Salles archivées"
              icon={Archive}
              salles={archivedSalles}
              isArchived={true}
              onRestore={(salle) => openConfirmDialog(
                'Restaurer la salle',
                `Êtes-vous sûr de vouloir restaurer la salle "${salle.libelle}" ?`,
                handleRestoreSalle,
                salle
              )}
              onDelete={(salle) => openConfirmDialog(
                'Supprimer la salle',
                `Êtes-vous sûr de vouloir supprimer définitivement la salle "${salle.libelle}" ? Cette action est irréversible.`,
                handleDeleteSalle,
                salle
              )}
            />

            {filteredSalles.length === 0 && (
              <div className="bg-white rounded-xl shadow-md p-6 text-center">
                <Search className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <h2 className="text-lg font-semibold text-gray-700 mb-2">Aucun résultat trouvé</h2>
                <p className="text-gray-500">
                  Aucune salle ne correspond à votre recherche "{debouncedSearchTerm}".
                </p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Effacer la recherche
                </button>
              </div>
            )}
          </div>
        )}

        <SalleForm
          isOpen={dialogOpen}
          isEdit={isEdit}
          formData={formData}
          onClose={handleCloseDialog}
          onSubmit={handleSubmit}
          onChange={handleInputChange}
          loading={loading}
        />

        <ConfirmDialog
          isOpen={confirmDialog.open}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={() => confirmDialog.action(confirmDialog.salle)}
          onCancel={closeConfirmDialog}
          loading={loading}
        />
      </div>
    </Layout>
  );
};

export default SallePage;
